import React from "react";
import PropTypes from "prop-types";
import Modal from "react-modal";
import {
  Button,
  Card,
  CardBody,
  CardHeader,
  CardFooter,
  Collapse,
  Form,
  FormGroup,
  Label,
  Input,
  Col,
} from "reactstrap";
import { css } from "react-emotion";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import _ from "lodash";
import verificationApi from "@/api/verification";
import swal from "sweetalert2";
import { handleError } from "@/util/errorHandler";

const modalStyles = css`
  position: absolute;
  top: 50%;
  left: 50%;
  right: auto;
  bottom: auto;
  overflow: visible;
  margin-right: -50%;
  transform: translate(-50%, -50%);
  width: 50rem;
  max-height: 90vh;
  overflow-y: auto;

  .buttons {
    display: flex;
    justify-content: space-between;
    margin: 15px 0 0;
  }
`;

const overlayStyles = css`
  position: fixed;
  top: 0px;
  left: 0px;
  right: 0px;
  bottom: 0px;
  background-color: rgba(255, 255, 255, 0.75);
  z-index: 1021;
`;

const datePickerWrapper = css`
  width: 100%;
`;

const DATE_KEY = "departureDate";

const humanizeKey = key =>
  String(key)
    .replace(/[_-]+/g, " ")
    .replace(/([a-z])([A-Z])/g, "$1 $2")
    .replace(/\b\w/g, c => c.toUpperCase());

const isPlainObject = value =>
  value !== null && typeof value === "object" && !Array.isArray(value);

const formatDate = date => {
  if (!date) return null;
  const d = new Date(date);
  if (isNaN(d.getTime())) return null;
  const pad = v => (v > 9 ? v : `0${v}`);
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
};

const isDateLikeValue = value => {
  if (value == null || value === "") return true;
  if (value instanceof Date) return !isNaN(value.getTime());
  if (typeof value !== "string") return false;
  return !isNaN(new Date(value).getTime());
};

class UpdateAdditionalInfoModal extends React.Component {
  state = {
    formData: this.props.data || {},
    expanded: {},
  };

  componentDidUpdate(prevProps) {
    if (prevProps.data !== this.props.data) {
      this.setState({ formData: this.props.data || {}, expanded: {} });
    }
  }

  isExpanded = pathStr => {
    const v = this.state.expanded[pathStr];
    return v === undefined ? true : v;
  };

  toggleExpanded = pathStr => {
    this.setState(prev => ({
      expanded: {
        ...prev.expanded,
        [pathStr]:
          prev.expanded[pathStr] === undefined
            ? false
            : !prev.expanded[pathStr],
      },
    }));
  };

  setValueAtPath = (path, value) => {
    this.setState(prev => {
      const next = _.cloneDeep(prev.formData);
      _.set(next, path, value);
      return { formData: next };
    });
  };

  onSubmit = async e => {
    e.preventDefault();
    const accountId = this.props.userAccountId;
    const additionalInfo = _.cloneDeep(this.state.formData);

    if (
      Object.prototype.hasOwnProperty.call(additionalInfo, DATE_KEY) &&
      additionalInfo[DATE_KEY] instanceof Date
    ) {
      additionalInfo[DATE_KEY] = formatDate(additionalInfo[DATE_KEY]);
    }

    try {
      await verificationApi.updateAdditionalInfo({ accountId, additionalInfo });
      swal(
        "Success",
        "Additional info has been updated successfully",
        "success",
      );
    } catch (error) {
      handleError(error);
    }
  };

  reset = () => {
    this.setState({ formData: this.props.data || {} });
  };

  beforeClose = () => {
    this.reset();
    this.props.onModalClose();
  };

  renderDatePicker = (path, label, value) => {
    const selected =
      value instanceof Date ? value : value ? new Date(value) : null;
    return (
      <FormGroup row key={path}>
        <Label sm={3}>{label}</Label>
        <Col sm={9}>
          <DatePicker
            selected={selected && !isNaN(selected.getTime()) ? selected : null}
            className="form-control"
            wrapperClassName={datePickerWrapper}
            dateFormat="dd/MM/yyyy"
            onChange={date => this.setValueAtPath(path, date)}
          />
        </Col>
      </FormGroup>
    );
  };

  renderTextField = (path, label, value) => (
    <FormGroup row key={path}>
      <Label sm={3}>{label}</Label>
      <Col sm={9}>
        <Input
          type="text"
          value={value == null ? "" : String(value)}
          onChange={e => this.setValueAtPath(path, e.target.value)}
        />
      </Col>
    </FormGroup>
  );

  renderFields = (data, parentPath = []) => {
    if (!isPlainObject(data)) return null;
    const keys = Object.keys(data);
    if (parentPath.length === 0 && keys.includes(DATE_KEY)) {
      const rest = keys.filter(k => k !== DATE_KEY);
      keys.length = 0;
      keys.push(DATE_KEY, ...rest);
    }
    return keys.map(key => {
      const value = data[key];
      const path = [...parentPath, key];
      const pathStr = path.join(".");
      const label = humanizeKey(key);

      if (
        key === DATE_KEY &&
        parentPath.length === 0 &&
        isDateLikeValue(value)
      ) {
        return this.renderDatePicker(pathStr, label, value);
      }

      if (isPlainObject(value)) {
        const isOpen = this.isExpanded(pathStr);
        return (
          <div key={pathStr} className="mb-2">
            <div
              onClick={() => this.toggleExpanded(pathStr)}
              style={{
                cursor: "pointer",
                padding: "8px 0",
                fontWeight: "bold",
                userSelect: "none",
              }}>
              <i
                className={`fa ${
                  isOpen ? "fa-chevron-down" : "fa-chevron-right"
                }`}
                style={{ marginRight: 8 }}
              />
              {label}
            </div>
            <Collapse isOpen={isOpen}>
              <div
                style={{
                  paddingLeft: 16,
                  borderLeft: "2px solid #eee",
                  marginLeft: 4,
                }}>
                {this.renderFields(value, path)}
              </div>
            </Collapse>
          </div>
        );
      }

      return this.renderTextField(pathStr, label, value);
    });
  };

  render() {
    const { formData } = this.state;

    return (
      <Modal
        isOpen={this.props.isOpen}
        onRequestClose={this.beforeClose}
        className={modalStyles}
        overlayClassName={overlayStyles}
        shouldCloseOnOverlayClick={false}
        shouldCloseOnEsc={false}>
        <Card>
          <CardHeader>
            <Button close onClick={this.beforeClose} />
            <span style={{ color: "#D15050", fontWeight: "bold" }}>
              Update Additional Info
            </span>
          </CardHeader>
          <Form onSubmit={this.onSubmit}>
            <CardBody>{this.renderFields(formData)}</CardBody>
            <CardFooter>
              <div className="buttons">
                <Button color="primary" type="submit">
                  <span>Update</span>
                </Button>
              </div>
            </CardFooter>
          </Form>
        </Card>
      </Modal>
    );
  }
}

UpdateAdditionalInfoModal.defaultProps = {
  isOpen: false,
  onModalClose: () => {},
  data: {},
  userAccountId: null,
};

UpdateAdditionalInfoModal.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  onModalClose: PropTypes.func.isRequired,
  data: PropTypes.object.isRequired,
};

export default UpdateAdditionalInfoModal;
